/**
 * Interface for dependency-injected logging across SDK packages.
 *
 * SDK packages use this interface for operational logging (e.g., cache hits,
 * retry attempts, resolution steps). By default, a no-op logger is used so
 * logging has zero cost unless the consuming application opts in.
 *
 * Pass a logger via {@link init | init({ logger })} to enable logging.
 *
 * @example Using console as a quick logger
 * ```typescript
 * import { init } from '@hopdrive/sdk-core'
 *
 * init({
 *   executor: myExecutor,
 *   logger: console, // console satisfies Logger
 * })
 * ```
 *
 * @example Using a custom logger
 * ```typescript
 * import { init, type Logger } from '@hopdrive/sdk-core'
 *
 * const logger: Logger = {
 *   info:  (msg, ...data) => myLogger.info(msg, ...data),
 *   warn:  (msg, ...data) => myLogger.warn(msg, ...data),
 *   error: (msg, ...data) => myLogger.error(msg, ...data),
 *   debug: (msg, ...data) => myLogger.debug(msg, ...data),
 * }
 *
 * init({ executor: myExecutor, logger })
 * ```
 */
export interface Logger {
  info(message: string, ...data: unknown[]): void
  warn(message: string, ...data: unknown[]): void
  error(message: string, ...data: unknown[]): void
  debug(message: string, ...data: unknown[]): void
}

const noopLogger: Logger = {
  info: () => {},
  warn: () => {},
  error: () => {},
  debug: () => {},
}

let sdkLogger: Logger = noopLogger

export function setLogger(logger: Logger): void {
  sdkLogger = logger
}

/**
 * Returns the current SDK logger instance.
 *
 * If no logger was provided via {@link init | init({ logger })}, returns a
 * no-op logger whose methods silently do nothing.
 *
 * SDK package authors should call this to obtain the logger rather than
 * accepting a logger parameter on every function.
 *
 * @example
 * ```typescript
 * import { getLogger } from '@hopdrive/sdk-core'
 *
 * const log = getLogger()
 * log.info('Processing started', { moveId: 42 })
 * ```
 */
export function getLogger(): Logger {
  return sdkLogger
}

/**
 * Resets the SDK logger back to the default no-op logger.
 *
 * Intended for use in test teardown to prevent logger state from leaking
 * between tests.
 *
 * @example
 * ```typescript
 * import { resetLogger } from '@hopdrive/sdk-core'
 *
 * afterEach(() => {
 *   resetLogger()
 * })
 * ```
 */
export function resetLogger(): void {
  sdkLogger = noopLogger
}
